
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePassword } from '@/contexts';
import { AlertTriangle, Check, X, Shield } from "lucide-react";

interface PasswordRequirementProps {
  fulfilled: boolean;
  text: string;
}

interface ResetPasswordFormProps {
  onBack: () => void;
  onSuccess?: () => void;
}

const RequirementCheck: React.FC<PasswordRequirementProps> = ({ fulfilled, text }) => {
  return (
    <div className="flex items-center gap-2">
      {fulfilled ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <X className="h-3 w-3 text-red-500" />
      )}
      <span className={fulfilled ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
        {text}
      </span>
    </div>
  );
};

const ResetPasswordForm = ({ onBack, onSuccess }: ResetPasswordFormProps) => {
  const { resetPassword, hasPassword, passwordStrength } = usePassword();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState("");
  
  // Password requirement checks
  const hasMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword;
  
  const allRequirementsMet = 
    hasMinLength && 
    hasUppercase && 
    hasLowercase && 
    hasNumber && 
    hasSpecial && 
    passwordsMatch && 
    confirmPassword !== "";

  const handlePasswordReset = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      return;
    }
    
    setIsResetting(true);
    setError("");
    
    try {
      const success = resetPassword(hasPassword ? currentPassword : null, newPassword);
      
      if (success) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        
        if (onSuccess) {
          onSuccess();
        } else {
          onBack();
        }
      } else {
        // The error will be displayed by the toast from the resetPassword function
      }
    } catch (err) {
      setError("An error occurred while resetting your password");
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <form onSubmit={handlePasswordReset} className="space-y-4 text-left">
      {hasPassword && (
        <div className="space-y-2">
          <Label htmlFor="current-password">Current Password</Label>
          <Input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            placeholder="Enter your current password"
            dir="ltr"
            className="text-left"
          />
        </div>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="new-password">New Password</Label>
        <Input
          id="new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onFocus={() => setPasswordFocused(true)}
          onBlur={() => setPasswordFocused(false)}
          required
          placeholder="Enter a new password"
          dir="ltr"
          className="text-left"
        />
        
        {newPassword && (
          <div className="flex items-center gap-2 mt-1">
            {allRequirementsMet ? (
              <div className="text-xs text-green-500 flex items-center gap-1">
                <Check className="h-3 w-3" />
                <span>Strong password</span>
              </div>
            ) : (
              <div className="text-xs text-amber-500 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                <span>Password doesn't meet all requirements</span>
              </div>
            )}
          </div>
        )}
        
        {passwordFocused && (
          <div className="text-xs space-y-1 mt-2 p-2 border rounded-md bg-background text-left">
            <h4 className="font-medium text-sm">Password Requirements:</h4>
            <div className="grid gap-1">
              <RequirementCheck 
                fulfilled={hasMinLength}
                text="At least 8 characters long"
              />
              <RequirementCheck 
                fulfilled={hasUppercase}
                text="At least one uppercase letter"
              />
              <RequirementCheck 
                fulfilled={hasLowercase}
                text="At least one lowercase letter"
              />
              <RequirementCheck 
                fulfilled={hasNumber}
                text="At least one number"
              />
              <RequirementCheck 
                fulfilled={hasSpecial}
                text="At least one special character"
              />
            </div>
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="confirm-password">Confirm Password</Label>
        <Input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          placeholder="Confirm your password"
          dir="ltr"
          className="text-left"
        />
        {newPassword && confirmPassword && newPassword !== confirmPassword && (
          <div className="flex items-center gap-1 text-xs text-red-500 mt-1">
            <X className="h-3 w-3" />
            <span>Passwords do not match</span>
          </div>
        )}
      </div>
      
      {error && (
        <div className="flex items-center gap-2 text-red-500 mt-2">
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
      
      <div className="flex flex-col md:flex-row gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="w-full"
          disabled={isResetting}
        >
          Back
        </Button>
        <Button
          type="submit"
          className="w-full bg-nalc-teal hover:bg-nalc-teal/90"
          disabled={isResetting || !allRequirementsMet || (hasPassword && !currentPassword)}
        >
          {isResetting ? "Resetting..." : "Reset Password"}
        </Button>
      </div>
    </form>
  );
};

export default ResetPasswordForm;
